import { BaseComponent } from './base-component.js'
import { customElement, property, query, state } from 'lit/decorators.js'
import { html, css, TemplateResult, CSSResultGroup, nothing } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { classMap } from 'lit/directives/class-map.js'

type ProductCardVariant = 'small' | 'base' | 'vertical'
type ProductCardFooterLayout = 'horizontal' | 'vertical'

@customElement('il-product-card')
export default class ProductCardComponent extends BaseComponent {
  @property({ type: Boolean }) disabled: boolean = false
  @property({ type: Boolean, attribute: 'has-error', reflect: true }) hasError: boolean = false
  @property({ type: Boolean, attribute: 'fill-height', reflect: true }) fillHeight: boolean = false
  @property({ type: String }) variant: ProductCardVariant = 'base'
  @property({ type: String }) name: string = ''
  @property({ type: String }) description: string = ''
  @property({ type: String, attribute: 'hint-text' }) hintText: string = ''
  @property({ type: String, attribute: 'footer-layout' }) footerLayout: ProductCardFooterLayout = 'horizontal'
  @property({ type: Array }) images: string[] = []
  @property({ type: Array }) tags: string[] = []
  @property({ type: Object }) onClick?: () => void
  @property({ type: String, attribute: 'show-more-text' }) showMoreText: string = 'More Details'
  @property({ type: String, attribute: 'show-less-text' }) showLessText: string = 'Less Details'

  @query('slot[name="footer-left"]') private footerLeftSlot?: HTMLSlotElement
  @query('slot[name="footer-right"]') private footerRightSlot?: HTMLSlotElement

  @state() isExpanded: boolean = false
  @state() showExpandButton: boolean = false

  get shouldRenderBody(): boolean {
    return Boolean(this.description) || Boolean(this.hintText)
  }

  get shouldRenderTags(): boolean {
    return Boolean(this.tags.length)
  }

  get shouldRenderFooter(): boolean {
    const left = this.footerLeftSlot?.assignedElements({ flatten: true }) ?? []
    const right = this.footerRightSlot?.assignedElements({ flatten: true }) ?? []
    return left.length > 0 || right.length > 0
  }

  override firstUpdated() {
    const element = this.renderRoot?.querySelector('.il-product-card__description')
    if (!element) return

    if (element.scrollHeight > element.clientHeight) {
      this.showExpandButton = true
    }
  }

  private onFooterSlotChange = () => {
    this.requestUpdate()
  }

  toggleExpand(e: MouseEvent): void {
    if (this.showExpandButton) {
      e.stopPropagation()
      e.preventDefault()
      this.isExpanded = !this.isExpanded
    }
  }

  override render(): TemplateResult {
    const cardClasses = {
      'il-product-card': true,
      [`il-product-card--${this.variant}`]: true,
      [`il-product-card--footer--${this.footerLayout}`]: true,
      'il-product-card--disabled': this.disabled,
      'il-product-card--has-body': this.shouldRenderBody,
      'il-product-card--has-tags': this.shouldRenderTags,
      'il-product-card--has-image': this.images.length,
      'il-product-card--has-error': this.hasError,
      'il-product-card--has-footer': this.shouldRenderFooter,
      'il-product-card--clickable': Boolean(this.onClick),
      'il-product-card--fill-height': this.fillHeight,
      'il-product-card--expandable': this.showExpandButton,
      'il-product-card--expanded': this.isExpanded,
    }

    return html`
      <div data-testid="iliad-product-card" class="${classMap(cardClasses)}" @click="${() => this.onClick?.()}">
        <image data-testid="iliad-product-card__image" class="il-product-card__image" src="${this.images[0]}" alt="${this.name}" />

        <div data-testid="iliad-product-card__content" class="il-product-card__content">
          <div class="il-product-card__header">
            <div part="header-top" class="il-product-card__header-top">
              <h2 class="il-product-card__name">${this.name}</h2>
              <slot name="header-right"></slot>
            </div>

            <div data-testid="iliad-product-card__tags" class="il-product-card__tags">
              ${this.tags.map((tag) => html`<il-tag size="small" version="bold">${tag}</il-tag>`)}
            </div>
          </div>

          <div class="il-product-card__body" .role="${this.showExpandButton ? 'button' : ''}" @click="${this.toggleExpand}" >
            <div class="il-product-card__body-left markdown">
              ${this.description ? html`<div class="il-product-card__description">${unsafeHTML(this.description ?? '')}</div>` : nothing}
              ${this.hintText ? html`<div class="il-product-card__hint">${this.hintText}</div>` : nothing}
            </div>

            <div
              data-testid="iliad-product-card__expand-button"
              class="il-product-card__expand-button"
            >
              ${this.isExpanded ? this.showLessText : this.showMoreText}
            </div>
          </div>

          <div class="il-product-card__footer">
            <div class="il-product-card__footer-left">
              <slot name="footer-left" @slotchange=${this.onFooterSlotChange}></slot>
            </div>
            <div class="il-product-card__footer-right">
              <slot name="footer-right" @slotchange=${this.onFooterSlotChange}></slot>
            </div>
          </div>

          <slot name="after"></slot>
        </div>
      </div>
    `
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      :host {
        width: 100%;
      }

      :host([fill-height]) {
        height: 100%;
      }

      .il-product-card {
        display: flex;
        background: var(--il-color-neutrals-0);
        box-shadow: var(--il-shadow-cool-base);
        border-radius: 12px;
      }

      .il-product-card.il-product-card--vertical {
        flex-direction: column;
      }
      .il-product-card.il-product-card--clickable {
        cursor: pointer;
      }
      .il-product-card.il-product-card--fill-height {
        height: 100%;
      }
      .il-product-card.il-product-card--has-error {
        outline: 2px solid var(--il-color-warning);
      }

      .il-product-card--vertical .il-product-card__header-top {
        align-items: center;
      }

      .il-product-card__image {
        display: none;
        width: 152px;
        height: 152px;

        object-fit: cover;
        object-position: center;
        border-radius: 12px;
      }
      .il-product-card--has-image .il-product-card__image {
        display: block;
      }
      .il-product-card--small .il-product-card__image {
        width: 100px;
        height: 100px;
      }
      .il-product-card--vertical .il-product-card__image {
        width: 100%;
        height: auto;
        aspect-ratio: 3 / 2;
        border-radius: 12px 12px 0 0;
      }

      .il-product-card__content {
        height: 100%;
        width: 100%;

        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 16px;
      }

      .il-product-card--fill-height .il-product-card__content {
        justify-content: center;
      }

      .il-product-card__header {
        width: 100%;

        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .il-product-card__header-top {
        width: 100%;
        display: flex;
        justify-content: space-between;
        gap: 8px;
      }

      .il-product-card__name {
        font-size: var(--il-font-size-16);
        font-weight: var(--il-font-weight-xbold);
        line-height: var(--il-font-line-height-125);
        letter-spacing: var(--il-font-letter-spacing-2);
        color: var(--il-color-neutrals-350);

        margin: 0;
      }

      .il-product-card__tags {
        display: none;
        gap: 8px;
        flex-wrap: wrap;
      }
      .il-product-card--has-tags .il-product-card__tags {
        display: flex;
      }

      .il-product-card__body {
        display: none;
        flex-direction: column;
        gap: 8px;
      }
      .il-product-card--has-body .il-product-card__body {
        display: flex;
      }

      .il-product-card__body-left {
        display: flex;
        flex-direction: column;
        gap: 8px;

        font-size: var(--il-font-size-12);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
        color: var(--il-color-neutrals-300);
      }

      .il-product-card__description {
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
      }

      .il-product-card--expanded .il-product-card__description {
        -webkit-line-clamp: unset;
      }

      .il-product-card__expand-button {
        display: none;
        cursor: pointer;
        white-space: nowrap;
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-xbold);
        color: var(--il-color-neutrals-300);
      }

      .il-product-card--expandable .il-product-card__expand-button {
        display: flex;
      }

      .il-product-card__description {
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-2);
        color: var(--il-color-neutrals-300);
      }

      .il-product-card__description ul {
        margin: 0px;
      }

      .il-product-card__hint {
        font-size: var(--il-font-size-12);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
        color: var(--il-color-neutrals-300);
      }

      .il-product-card__footer {
        width: 100%;

        display: none;
        justify-content: space-between;
        grid-template-columns: auto auto;
        align-items: center;
        gap: 8px;
      }
      .il-product-card--has-footer .il-product-card__footer {
        display: grid;
      }
      .il-product-card--footer--vertical .il-product-card__footer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      .il-product-card__footer-left {
        grid-column: 1;
      }
      .il-product-card__footer-right {
        grid-column: 2;
      }
    `
  ]
}

declare global {
  interface HTMLElementTagNameMap {
    'il-product-card': ProductCardComponent
  }
}
